import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Area, AreaPagingRes,UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError} from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchAreas = (params?: GetAreasParams, enabled = true) => {
  return useQuery({
    queryKey: ['adminAreas', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getAreas(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetArea = (id: string) => {
  return useQuery({
    queryKey: ['adminAreas', id],
    queryFn: async () => {
      return getArea(id);
    },
  });
};

export const useCreateArea = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createArea,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAreas'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Area creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};


export const useUpdateArea = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateArea,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAreas'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Areas actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetAreasParams = Partial<Area> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateAreaParams = Omit<Area, 'id_area'>;
export type UpdateAreaParams = {
  id: string;
  data: CreateAreaParams;
};

export const getAreas = async (params?: GetAreasParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<AreaPagingRes>(
    `${urlBackend}api/v1/area/?${queryParams}`,
    true
  );
};

export const getArea = async (id: string) => {
  try{
    return await get<Area>(`${urlBackend}api/v1/area/${id}/`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createArea = async (data: CreateAreaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Area>(`${urlBackend}api/v1/area/`, data, true);
};

export const updateArea = async ({ id, data }: UpdateAreaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Area>(`${urlBackend}api/v1/area/${id}/`, data, true);
};
